const DIALOG_ID = 'awp-quick-add-dialog';

class AwpQuickAddTrigger extends HTMLElement {
  /** @type {number} */
  #hoverTimer = 0;

  connectedCallback() {
    const button = this.querySelector('button');
    if (!button) return;
    button.addEventListener('click', this.#handleClick);
    // Warm the modal's cache before the click so it can render instantly.
    // Hover uses a short intent delay so cursor pass-throughs don't fetch;
    // touch/focus prefetch immediately for the earliest possible head start.
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('touchstart', this.#prefetch, { passive: true });
    this.addEventListener('focusin', this.#prefetch);
  }

  disconnectedCallback() {
    clearTimeout(this.#hoverTimer);
    const button = this.querySelector('button');
    if (button) button.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('touchstart', this.#prefetch);
    this.removeEventListener('focusin', this.#prefetch);
  }

  #handlePointerEnter = () => {
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(this.#prefetch, 80);
  };

  #handlePointerLeave = () => {
    clearTimeout(this.#hoverTimer);
  };

  #prefetch = () => {
    const dialog = document.getElementById(DIALOG_ID);
    if (!(dialog instanceof AwpQuickAddDialog)) return;
    const productUrl = this.dataset.productUrl;
    if (productUrl) dialog.prefetch(productUrl);
  };

  #handleClick = (event) => {
    event.preventDefault();
    const dialog = document.getElementById(DIALOG_ID);
    if (!(dialog instanceof AwpQuickAddDialog)) return;

    const productUrl = this.dataset.productUrl;
    const productTitle = this.dataset.productTitle || '';
    if (!productUrl) return;

    dialog.openFor({ productUrl, productTitle });
  };
}

if (!customElements.get('awp-quick-add-trigger')) {
  customElements.define('awp-quick-add-trigger', AwpQuickAddTrigger);
}

class AwpQuickAddDialog extends HTMLElement {
  /** @type {Map<string, DocumentFragment>} */
  #cache = new Map();
  /** @type {Map<string, Promise<DocumentFragment>>} */
  #inflight = new Map();
  /** @type {MediaQueryList | null} */
  #mobileMq = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.contentEl = this.querySelector('[data-awp-qa-content]');
    this.loadingEl = this.querySelector('[data-awp-qa-loading]');
    this.titleEl = this.querySelector('[data-awp-qa-title]');
    this.priceEl = this.querySelector('[data-awp-qa-price]');
    this.imageEl = this.querySelector('[data-awp-qa-image]');
    this.imageLinkEl = this.querySelector('[data-awp-qa-image-link]');
    this.sizeSlotEl = this.querySelector('[data-awp-qa-size-slot]');

    this.querySelector('[data-awp-qa-close]')?.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', this.#handleBackdropClick);
    this.dialog.addEventListener('click', this.#handleSizeSelectorClick);
    this.dialog.addEventListener('close', this.#handleClose);

    this.addEventListener('cart:update', this.#handleCartUpdate);
    this.addEventListener('cart-update', this.#handleCartUpdate);

    this.#mobileMq = window.matchMedia('(max-width: 749px)');
    this.#mobileMq.addEventListener('change', this.#handleViewportChange);
  }

  disconnectedCallback() {
    this.#mobileMq?.removeEventListener('change', this.#handleViewportChange);
  }

  /**
   * Warm the cache for a product URL without opening the modal. Safe to call
   * repeatedly (hover, focus, touch) — cached and in-flight URLs are skipped.
   * @param {string} url
   */
  prefetch(url) {
    if (!url || this.#cache.has(url) || this.#inflight.has(url)) return;
    // Fire-and-forget; swallow errors so a failed prefetch retries on click.
    this.#loadFragment(url).catch(() => {});
  }

  #handleViewportChange = () => {
    const form = this.contentEl?.querySelector('awp-product-form');
    if (form) this.#placeSizeSelector(form);
  };

  /**
   * @param {{ productUrl: string, productTitle?: string }} args
   */
  async openFor({ productUrl, productTitle = '' }) {
    if (!this.dialog) return;

    this.#setLoading();
    if (this.titleEl) {
      if (productTitle) {
        // Once the real title is set it becomes the link's accessible name;
        // drop the static fallback aria-label so it doesn't override it.
        this.titleEl.textContent = productTitle;
        this.titleEl.removeAttribute('aria-label');
      }
      this.titleEl.setAttribute('href', productUrl);
    }
    if (this.imageLinkEl) this.imageLinkEl.setAttribute('href', productUrl);
    if (this.imageEl instanceof HTMLImageElement) {
      this.imageEl.removeAttribute('src');
      if (productTitle) this.imageEl.alt = productTitle;
    }
    if (this.priceEl) this.priceEl.textContent = '';

    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.body.style.overflow = 'hidden';

    try {
      const fragment = await this.#getFragment(productUrl);
      this.#render(fragment);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('AWP Quick Add: failed to load product', error);
      this.#setError();
    }
  }

  close() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
      this.#handleClose();
    }
  }

  #handleClose = () => {
    document.body.style.overflow = '';
  };

  #handleBackdropClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  #handleCartUpdate = (event) => {
    const didError = event?.detail?.data?.didError;
    if (didError) return;
    this.close();
  };

  #setLoading() {
    if (!this.contentEl) return;
    this.contentEl.innerHTML = '';
    const loading = document.createElement('div');
    loading.className = 'awp-quick-add-modal__loading';
    loading.innerHTML = '<span class="awp-quick-add-modal__spinner"></span><span>Loading…</span>';
    this.contentEl.appendChild(loading);
  }

  #setError() {
    if (!this.contentEl) return;
    this.contentEl.innerHTML = '<div class="awp-quick-add-modal__error">Unable to load product. Please try again.</div>';
  }

  /**
   * @param {string} url
   * @returns {Promise<DocumentFragment>}
   */
  async #getFragment(url) {
    const fragment = await this.#loadFragment(url);
    return fragment.cloneNode(true);
  }

  /**
   * Fetches + parses the product page once per URL, caching the result and
   * deduping concurrent requests so a hover-prefetch and the click that
   * follows share a single network round-trip. Returns the cached fragment
   * (callers clone it before use).
   * @param {string} url
   * @returns {Promise<DocumentFragment>}
   */
  #loadFragment(url) {
    const cached = this.#cache.get(url);
    if (cached) return Promise.resolve(cached);

    const existing = this.#inflight.get(url);
    if (existing) return existing;

    const promise = (async () => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const fragment = document.createDocumentFragment();
      const form = doc.querySelector('awp-product-form');
      if (form) fragment.appendChild(form);

      const price = doc.querySelector('product-price') || doc.querySelector('.price');
      if (price) fragment.appendChild(this.#wrapForSlot(price, 'price'));

      const title = this.#findProductTitle(doc);
      if (title) {
        const meta = document.createElement('meta');
        meta.setAttribute('data-awp-qa-meta-title', title);
        fragment.appendChild(meta);
      }

      const imgSrc = this.#findProductImage(doc);
      if (imgSrc) {
        const meta = document.createElement('meta');
        meta.setAttribute('data-awp-qa-meta-image', imgSrc);
        fragment.appendChild(meta);
      }

      this.#cache.set(url, fragment);
      return fragment;
    })();

    this.#inflight.set(url, promise);
    promise.finally(() => this.#inflight.delete(url));
    return promise;
  }

  /**
   * @param {Element} node
   * @param {string} slot
   */
  #wrapForSlot(node, slot) {
    const wrapper = document.createElement('div');
    wrapper.setAttribute('data-awp-qa-slot', slot);
    wrapper.appendChild(node);
    return wrapper;
  }

  /**
   * @param {Document} doc
   * @returns {string | null}
   */
  #findProductTitle(doc) {
    const selectors = [
      '.view-product-title a',
      '.product-information h1',
      'product-title',
    ];
    for (const sel of selectors) {
      const el = doc.querySelector(sel);
      const text = el?.textContent?.trim();
      if (text) return text;
    }
    return null;
  }

  /**
   * @param {Document} doc
   * @returns {string | null}
   */
  #findProductImage(doc) {
    const selectors = [
      '.product-information__media img',
      '[data-product-grid-content] img',
      'product-media img',
      'media-gallery img',
    ];
    for (const sel of selectors) {
      const img = doc.querySelector(sel);
      if (img instanceof HTMLImageElement && img.src) return img.src;
      if (img?.getAttribute('src')) return img.getAttribute('src');
    }
    return null;
  }

  /**
   * @param {DocumentFragment} fragment
   */
  #render(fragment) {
    if (!this.contentEl) return;

    const form = fragment.querySelector('awp-product-form');
    const priceSlot = fragment.querySelector('[data-awp-qa-slot="price"]');
    const metaImage = fragment.querySelector('[data-awp-qa-meta-image]');
    const metaTitle = fragment.querySelector('[data-awp-qa-meta-title]');

    this.contentEl.innerHTML = '';
    if (this.sizeSlotEl) this.sizeSlotEl.innerHTML = '';

    if (form) {
      this.contentEl.appendChild(form);
    } else {
      this.#setError();
      return;
    }

    this.#placeSizeSelector(form);

    if (this.priceEl) {
      this.priceEl.innerHTML = '';
      if (priceSlot) {
        while (priceSlot.firstChild) this.priceEl.appendChild(priceSlot.firstChild);
      }
    }

    if (metaTitle && this.titleEl) {
      const title = metaTitle.getAttribute('data-awp-qa-meta-title');
      if (title) {
        this.titleEl.textContent = title;
        if (this.imageEl instanceof HTMLImageElement) this.imageEl.alt = title;
      }
    }

    if (this.imageEl instanceof HTMLImageElement && metaImage) {
      const src = metaImage.getAttribute('data-awp-qa-meta-image');
      if (src) this.imageEl.src = src;
    }

    this.#listenForAwpCartAdd(form);
    this.#initGrindDropdown(this.contentEl);
    this.#swapMultiAddressLink(form);
  }

  /**
   * Move size selector to header on desktop, leave it in the form on mobile
   * (so it sits on the same row as the quantity selector).
   * @param {Element} form
   */
  #placeSizeSelector(form) {
    if (!this.sizeSlotEl) return;
    const sizeSelector =
      form.querySelector('.awp_size-selector') ||
      this.sizeSlotEl.querySelector('.awp_size-selector');
    if (!sizeSelector) return;

    const isMobile = this.#mobileMq?.matches ?? false;
    if (isMobile) {
      if (sizeSelector.parentElement === this.sizeSlotEl) {
        const innerForm = form.querySelector('form');
        if (innerForm) innerForm.prepend(sizeSelector);
      }
    } else if (sizeSelector.parentElement !== this.sizeSlotEl) {
      this.sizeSlotEl.appendChild(sizeSelector);
    }
  }

  /**
   * In the modal, repurpose the "Send to Multiple Addresses" link as a link to the PDP.
   * @param {Element} form
   */
  #swapMultiAddressLink(form) {
    const link = form.querySelector('.awp_multi-address__link');
    if (!(link instanceof HTMLAnchorElement)) return;
    const productUrl = this.titleEl?.getAttribute('href');
    if (productUrl) link.href = productUrl;
    link.textContent = 'View Full Product Details →';
  }

  /**
   * Keeps size-selector clicks inside the modal. Delegated on the dialog so it
   * works no matter where the selector currently sits — the header slot on
   * desktop or inside the form body on mobile (it gets moved between the two).
   * @param {MouseEvent} event
   */
  #handleSizeSelectorClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const btn = target?.closest?.('.awp_size-selector__btn');
    if (!(btn instanceof HTMLAnchorElement) || !this.dialog?.contains(btn)) return;
    event.preventDefault();
    if (btn.classList.contains('awp_size-selector__btn--active')) return;
    this.openFor({ productUrl: btn.href });
  };

  /**
   * @param {Element} container
   */
  #initGrindDropdown(container) {
    if (!container) return;
    container.querySelectorAll('.awp_grind-selector').forEach((grindEl) => {
      const hiddenSelect = grindEl.querySelector('.awp_grind-selector__select--hidden');
      const custom = grindEl.querySelector('.awp_grind-selector__custom');
      const trigger = grindEl.querySelector('.awp_grind-selector__trigger');
      const triggerText = grindEl.querySelector('.awp_grind-selector__trigger-text');
      const options = grindEl.querySelectorAll('.awp_grind-selector__option');

      if (!hiddenSelect || !custom || !trigger || !triggerText) return;

      trigger.addEventListener('click', () => {
        const isOpen = custom.classList.contains('awp_grind-selector__custom--open');
        custom.classList.toggle('awp_grind-selector__custom--open');
        trigger.setAttribute('aria-expanded', String(!isOpen));
      });

      options.forEach((option) => {
        option.addEventListener('click', () => {
          const value = /** @type {HTMLElement} */ (option).dataset.value ?? '';
          triggerText.textContent = value;
          /** @type {HTMLSelectElement} */ (hiddenSelect).value = value;
          options.forEach((o) => {
            o.classList.remove('awp_grind-selector__option--selected');
            o.setAttribute('aria-selected', 'false');
          });
          option.classList.add('awp_grind-selector__option--selected');
          option.setAttribute('aria-selected', 'true');
          custom.classList.remove('awp_grind-selector__custom--open');
          trigger.setAttribute('aria-expanded', 'false');
          hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      document.addEventListener('click', (e) => {
        if (!custom.contains(/** @type {Node} */ (e.target))) {
          custom.classList.remove('awp_grind-selector__custom--open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          custom.classList.remove('awp_grind-selector__custom--open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /**
   * @param {Element} form
   */
  #listenForAwpCartAdd(form) {
    const handler = (event) => {
      const didError = event?.detail?.data?.didError ?? event?.detail?.didError;
      if (didError) return;
      this.close();
    };
    form.addEventListener('cart:update', handler);
    form.addEventListener('cart-update', handler);
    form.addEventListener('CartAddEvent', handler);
  }
}

if (!customElements.get('awp-quick-add-dialog')) {
  customElements.define('awp-quick-add-dialog', AwpQuickAddDialog);
}

// Product-recommendations sections with the "Card button opens quick-add modal"
// toggle carry [data-awp-card-quick-add]. There the card ATC renders as a plain
// link (a.awp_link_to_product); intercept it and open the shared modal instead of
// navigating. Scoped by the ancestor attribute, so other cards keep linking.
document.addEventListener('click', (event) => {
  const target = /** @type {Element} */ (event.target);
  const link = target?.closest?.('[data-awp-card-quick-add] a.awp_link_to_product');
  if (!link) return;
  const dialog = document.getElementById(DIALOG_ID);
  if (!(dialog instanceof AwpQuickAddDialog)) return;
  const productUrl = link.getAttribute('href');
  if (!productUrl) return;
  event.preventDefault();
  const card = link.closest('.product-card');
  const productTitle =
    card?.querySelector('.product-title, .product-card__title, h3')?.textContent?.trim() || '';
  dialog.openFor({ productUrl, productTitle });
});
